import {
    EzRegistrationState
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';

/**
 * Creates a new instance of EzHtmlBuilder
 * Assists in build html within Javascript code.
 *
 * @returns {EzHtmlBuilder}
 */
export class EzHtmlBuilder extends EzClass {
    ezHtml = '';

    /**
     * Builds a new elementId from the current date that should be 'mostly' unique.
     *
     * @returns {String}
     */
    ezNewElementId() {
        const cDate = new Date();

        return `EzHtmlBuilder${cDate.getUTCFullYear()}${cDate.getUTCMonth()}${cDate.getUTCDay()}` +
            `${cDate.getUTCHours()}${cDate.getUTCMinutes()}${cDate.getUTCSeconds()}${cDate.getUTCMilliseconds()}`;
    }

    /**
     * @public
     * Returns the provided aId if not null, undefined, or empty. Otherwise, returns this.ezNewElementId() result.
     *
     * @param {String|null|undefined} aId
     *
     * @returns {String}
     */
    ezElementId(aId) {
        return 'undefined=' === aId || null === aId || 0 === aId.length
            ? this.ezNewElementId()
            : aId;
    }

    /**
     * @public
     * Creates a new props object and it's id property is set equal to EzHtmlBuilder.ezElementId(aId)
     *
     * @param {String|undefined|null} aId
     *
     * @returns {String}
     */
    ezCreateProps(aId) {
        return null === aId || 'undefined=' === aId || 0 >= aId.length
            ? {
                id: this.ezNewElementId()
            }
            : {
                id: this.ezElementId(aId)
            };
    }

    /**
     * If the aProps is null or undefined then ezCreateProps is set equal to EzHtmlBuilder.ezCreateProps(aId)
     * If aProps's id property is null, undefined, or empty property then set to EzHtmlBuilder.ezElementId(aId).
     *
     * Finally, the aProps object is returned.
     *
     * @param {String|null|undefined} aId
     * @param {Object|null|undefined} aProps
     *
     * @returns {String}
     */
    ezProps(aProps, aId) {
        if (!aProps) {
            return this.ezCreateProps(aId);
        }

        if ('undfined' === aProps.id || null == aProps.id || 0 === aProps.id.length) {
            aProps.id = this.ezElementId(aId);
        }

        return aProps;
    }

    /**
     * Builds an HTML tag
     *
     * @param {*} elementName
     * @param {*} elementProps
     *
     * @returns {String}
     * "<{elementName {elementProps...}>{elementProps.content}</elementName>"
     */
    ezElement(elementName, elementProps) {
        if (!elementName || 0 === elementName.lenth) {
            // Mising the element name
            throw new Error({
                errorCode: 500,
                message: 'A valid elementName param is required in call to EzHtmlBuilder.ezElement()'
            });
        }

        this.ezHtml += `<${elementName} `;

        let contentToUse = null;

        if (elementProps) {
            for (const propName in elementProps) {
                if (ezApi.ezHasOwnProperty(elementProps, propName)) {
                    const propValue = elementProps[propName];
                    if ('content' === propName) {
                        contentToUse = propValue;
                    } else {
                        this.ezHtml += propName;
                        this.ezHtml += null !== propValue
                            ? `="${propValue}" `
                            : ' ';
                    }
                }
            }
        }

        this.ezHtml += contentToUse && 0 !== contentToUse.length
            ? `>${contentToUse}</${elementName}>`
            : '/>';

        return this;
    };

    /**
     * @public
     * Builds the HTML <div> tag
     *
     * @param {*} id
     * @param {*} aProps
     *
     * @returns {String}
     * "<div {props...}>{props.content}</div>"
     */
    ezDiv(id, aProps) {
        this.ezElement('div', this.ezProps(aProps, id));
        return this;
    }

    /**
     * @public
     * Builds the HTML <button> tag.
     *
     * @param {String|null|undefined*} id
     * @param {*} aProps
     *
     * @returns {String}
     * "<button {props...}>{props.content}</button>"
     */
    ezButton(id, aProps) {
        this.ezElement('button', this.ezProps(aProps, id));
        return this;
    }

    ezTable(id, aProps) {
        this.ezElement('table', this.ezProps(aProps, id));
        return this;
    }

    ezTr(id, aProps) {
        this.ezElement('tr', this.ezProps(aProps, id));

        return this;
    }

    ezTd(id, aProps) {
        this.ezElement('td', this.ezProps(aProps, id));

        return this;
    }

    ezSelect(id, aProps) {
        this.ezElement('select', this.ezProps(aProps, id));
        return this;
    }

    ezOption(id, aProps) {
        this.ezElement('option', this.ezProps(aProps, id));
        return this;
    }

    ezSpan(id, aProps) {
        this.ezElement('span', this.ezProps(aProps, id));
        return this;
    }

    ezLabel(id, aProps) {
        this.ezElement('label', this.ezProps(aProps, id));
        return this;
    }

    /**
     * Appends the provided  rawText as-is to the html.
     * @param {String} rawText
     */
    ezRaw(rawText) {
        this.ezHtml += rawText;
    }

    /**
     * Builds the html <img> tag
     *
     * @param {String|null|undefined} id
     * @param {Object|null|undefined} aProps
     *
     * @returns {String}
     * "<img {aProps...} />"
     */
    ezImg(id, aProps) {
        aProps.content = null;
        this.ezElement('img', this.ezProps(aProps, id));
        return this;
    }

    /**
     * @public
     * Builds the HTML comment <!-- --> tag.
     *
     * @param {String|undefined|null} comment
     *
     * @returns {String}
     * "<!-- {comment} -->"
     */
    ezComment(comment) {
        if (!comment || 0 === comment.length) {
            this.html += '<!-- -->';
        }

        this.html += `<!-- ${comment} -->`;

        return this;
    }

    /**
     * @public
     * Returns the HTML string
     *
     * @returns {String}
     */
    ezBuild() {
        return this.ezHtml;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     *
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezHtmlBuilder';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     *
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzHtmlBuilder_Ready',
            onEmployerDashboardControllerInitialized: 'ezOn_EmployerDashboardController_Initialized'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with globalThis.ezApi.
     *
     * @type {EzHtmlBuilder}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzHtmlBuilder.ezApiName]
        ? globalThis.ezApi.ezclocker[EzHtmlBuilder.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     *
     * @returns {EzHtmlBuilder}
     */
    static get ezInstance() {
        return EzHtmlBuilder.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with globalThis.ezApi.
     *
     * @param {EzHtmlBuilder} instance
     */
    static set ezInstance(instance) {
        if (null != EzHtmlBuilder.#ezInstance) {
            throw new Error('EzHtmlBuilder\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzHtmlBuilder.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     *
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzHtmlBuilder.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     *
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzHtmlBuilder.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     *
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzHtmlBuilder.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     *
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzHtmlBuilder.ezApiRegistrationState &&
            globalThis?.ezApi?.ready && globalThis?.ezApi?.ezclocker;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     *
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzHtmlBuilder.ezInstance &&
            EzRegistrationState.REGISTERED === EzHtmlBuilder.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     *
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzHtmlBuilder.#ezCanRegister && !EzHtmlBuilder.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzHtmlBuilder, EzHtmlBuilder.ezApiName, false, 'ezHtml');
        }

        return EzHtmlBuilder.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzHtmlBuilder.ezApiName
     *     2) Property getter EzHtmlBuilder.ezEventNames
     *     3) Property getter EzHtmlBuilder.ezInstance
     *     4) Property setter EzHtmlBuilder.ezInstance
     *     5) Property getter EzHtmlBuilder.ezApiRegistrationState
     *     6) Property setter EzHtmlBuilder.ezApiRegistrationState
     *     7) Property getter EzHtmlBuilder.#ezCanRegister
     *     8) Property getter EzHtmlBuilder.#ezIsRegistered
     *     9) Method EzHtmlBuilder.#ezRegistrator()
     */
    static {
        if (!EzHtmlBuilder.#ezIsRegistered) {
            EzHtmlBuilder.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzHtmlBuilder.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzClass.ezOnEzApiReadyEventName,
                    EzHtmlBuilder.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

}
